import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  BackArrow,
  Button,
  Input,
  TopProgress,
  Typography,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { zonesService } from "../../../../api";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import { usePageTitle } from "../../../../context/navigationContext";
import {
  Container,
  Header,
  FormSection,
  FormActions,
} from "./styles";

type Zone = {
  id: string;
  name: string;
  email: string;
  description: string;
  ttl: number;
  type: string;
  status: string;
};

const ZoneEdit: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { zoneId } = useParams<{ zoneId: string }>();
  const { viewAlert } = useUi();
  usePageTitle(t("EDIT_DNS_ZONE"));

  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [zone, setZone] = useState<Zone | null>(null);
  const [editLoading, setEditLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!zoneId) {
      navigate("/openstack/zones");
      return;
    }

    setPageLoading(true);
    zonesService
      .get(zoneId)
      .then((res) => {
        const zoneData = res.data as Zone;
        setZone(zoneData);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_ZONE");
        viewAlert({
          severity: "error",
          message,
        });
        navigate("/openstack/zones");
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [zoneId, t, viewAlert, navigate]);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        email: yup.string().email(t("INVALID_EMAIL")).required(t("REQUIRED_FIELD")),
        ttl: yup.number().min(0, t("TTL_MIN_VALUE")).required(t("REQUIRED_FIELD")),
        description: yup.string().nullable(),
      }),
    [t]
  );

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      email: zone?.email || "",
      ttl: zone?.ttl || 1800,
      description: zone?.description || "",
    },
    onSubmit: (formValues) => {
      if (!zoneId) {
        return;
      }

      setEditLoading(true);
      setPageLoading(true);
      const payload = {
        email: formValues.email,
        ttl: formValues.ttl,
        description: formValues.description || "",
      };

      zonesService
        .update(zoneId, payload)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("ZONE_UPDATED_SUCCESSFULLY"),
          });
          navigate(`/openstack/zones/${zoneId}`);
        })
        .catch((error) => {
          const backendErrors = error?.response?.data;
          if (backendErrors?.detail) {
            viewAlert({
              severity: "error",
              message: backendErrors.detail,
            });
          } else if (backendErrors) {
            setErrors(backendErrors);
          } else {
            viewAlert({
              severity: "error",
              message: t("FAILED_TO_UPDATE_ZONE"),
            });
          }
        })
        .finally(() => {
          setEditLoading(false);
          setPageLoading(false);
        });
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setErrors,
  } = formik;

  const onFormSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      formik.submitForm();
    },
    [formik]
  );

  if (pageLoading || !zone) {
    return (
      <Container>
        <TopProgress loading={true} />
        <Typography variant="body">{t("LOADING")}</Typography>
      </Container>
    );
  }

  const formikProps = {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
  };

  return (
    <Container>
      <TopProgress loading={pageLoading} />
      <Header>
        <BackArrow fallbackPath={`/openstack/zones/${zoneId}`} />
        <Typography variant="h1">{t("EDIT_DNS_ZONE")}</Typography>
      </Header>
      <FormSection as="form" onSubmit={onFormSubmit}>
        <Input
          required
          title={t("DOMAIN_NAME")}
          placeholder=""
          fieldName="name"
          value={zone.name}
          disabled
        />

        <Input
          required
          title={t("ZONE_TYPE")}
          placeholder=""
          fieldName="type"
          value={zone.type}
          disabled
        />

        <Input
          required
          title={t("EMAIL")}
          placeholder="admin@example.com"
          fieldName="email"
          type="email"
          {...formikProps}
        />

        <Input
          title={t("DESCRIPTION")}
          placeholder=""
          fieldName="description"
          {...formikProps}
        />

        <Input
          required
          title={t("TTL")}
          placeholder="1800"
          fieldName="ttl"
          type="number"
          {...formikProps}
        />

        <FormActions>
          <Button
            variant="stroke"
            size="display"
            title={t("CANCEL")}
            onClick={() => navigate(`/openstack/zones/${zoneId}`)}
          />
          <Button
            variant="primary"
            size="display"
            title={t("SAVE")}
            loading={editLoading}
            onClick={onFormSubmit}
          />
        </FormActions>
      </FormSection>
    </Container>
  );
};

export default ZoneEdit;
